import { ref, onBeforeUnmount } from 'vue'
import { useFeatureFlags } from '@/composables/useFeatureFlags'

/**
 * Google reCAPTCHA v2 ("I'm not a robot"), loaded on demand.
 *
 * v2 rather than v3: v3 renders no widget and returns a score the *server* has to
 * threshold, which is a different control and a different backend contract. v2 gives a
 * token the register endpoint can verify outright, which is what the sign-up flow needs.
 *
 * The rest of the app only ever sees `token`, so swapping provider or version means
 * rewriting this file and nothing else.
 *
 * The site key comes from the API (see useFeatureFlags), which returns it only when both
 * the site key and the secret are configured on the server. With none the composable
 * reports `enabled: false` and the form skips
 * the widget entirely, so local development and tests need no Google account. The server
 * still decides whether a missing token is acceptable.
 */

const SCRIPT_ID = 'google-recaptcha-script'

/**
 * Explicit rendering needs an onload callback: `window.grecaptcha` can exist before
 * `grecaptcha.render` is usable, so waiting on the script's load event alone is a race.
 */
const ONLOAD_CALLBACK = '__vesprocRecaptchaOnload'
const SCRIPT_URL = `https://www.google.com/recaptcha/api.js?render=explicit&onload=${ONLOAD_CALLBACK}`

/** Module-level: the script is fetched once per page, however many forms ask for it. */
let scriptPromise = null

const loadScript = () => {
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha?.render) {
      resolve()
      return
    }

    window[ONLOAD_CALLBACK] = () => resolve()

    const existing = document.getElementById(SCRIPT_ID)
    if (existing) {
      // Already in flight from another mount; the shared onload above will settle it.
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA script failed to load')))
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_URL
    script.async = true
    script.defer = true
    script.onerror = () => {
      // Cleared so a later attempt can retry rather than await a promise that will
      // never settle — an ad blocker eating the script is the common case here.
      scriptPromise = null
      reject(new Error('reCAPTCHA script failed to load'))
    }
    document.head.appendChild(script)
  })

  return scriptPromise
}

export function useCaptcha() {
  const siteKey = useFeatureFlags().features.captchaSiteKey || ''
  const enabled = Boolean(siteKey)

  const token = ref('')
  const ready = ref(false)
  const failed = ref(false)

  let widgetId = null

  /** Mount the widget into `container`. No-op when no site key is configured. */
  const render = async container => {
    if (!enabled || !container) return
    try {
      await loadScript()
      widgetId = window.grecaptcha.render(container, {
        sitekey: siteKey,
        callback: value => {
          token.value = value
          failed.value = false
        },
        'expired-callback': () => {
          token.value = ''
        },
        'error-callback': () => {
          token.value = ''
          failed.value = true
        }
      })
      ready.value = true
    } catch {
      // A challenge that cannot load must not strand the supplier on a dead form: the
      // field reports its failure, and the server is the one that refuses the token.
      failed.value = true
      ready.value = false
    }
  }

  /** Issue a fresh challenge. Tokens are single-use, so call after every submit. */
  const reset = () => {
    token.value = ''
    failed.value = false
    if (widgetId !== null && window.grecaptcha?.reset) {
      window.grecaptcha.reset(widgetId)
    }
  }

  onBeforeUnmount(() => {
    // grecaptcha has no remove(): the widget dies with the container Vue is unmounting,
    // and resetting first stops a pending challenge from firing a callback into a
    // component that no longer exists.
    if (widgetId !== null && window.grecaptcha?.reset) {
      window.grecaptcha.reset(widgetId)
    }
    widgetId = null
  })

  return { enabled, token, ready, failed, render, reset }
}
